import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign, Users, TrendingDown, AlertTriangle, CreditCard, RefreshCw, ShoppingCart, Shield, ArrowRightLeft, Globe, Info } from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { ExportDropdown } from '@/components/ExportDropdown';
import { AIPanel } from '@/components/AIPanel';
import { MLInsightsPanel } from '@/components/MLInsightsPanel';
import SignalsWidget from '@/components/SignalsWidget';
import { CategoryPieChart } from '@/components/dashboard/CategoryPieChart';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { HelpBubble } from '@/components/HelpBubble';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SignalsPage from '@/pages/SignalsPage';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';
import { savingsBasisNote } from '@/lib/savingsBasis';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--destructive))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))', '#8b5cf6', '#06b6d4', '#f59e0b'];

export default function Dashboard() {
  const { symbol: cs } = useCurrency();
  const { filters, setFilters } = useGlobalFilters();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [savingsPotential, setSavingsPotential] = useState(null);
  const [p2pSummary, setP2pSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'signals' ? 'signals' : 'overview';
  const handleTabChange = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'signals') next.set('tab', 'signals'); else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  // Subscription check
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const subscribedFeatures = new Set(user?.subscribed_features || []);
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const hasP2P = isSuperAdmin || subscribedFeatures.has('p2p_analytics');

  // Build query string from filters
  const buildQueryString = useCallback((filterObj) => {
    const params = new URLSearchParams();
    if (filterObj.category) params.append('category', filterObj.category);
    if (filterObj.category_l2) params.append('category_l2', filterObj.category_l2);
    if (filterObj.category_l3) params.append('category_l3', filterObj.category_l3);
    if (filterObj.supplier) params.append('supplier', filterObj.supplier);
    if (filterObj.region) params.append('region', filterObj.region);
    if (filterObj.country) params.append('country', filterObj.country);
    if (filterObj.sbu) params.append('sbu', filterObj.sbu);
    if (filterObj.cost_center) params.append('cost_center', filterObj.cost_center);
    if (filterObj.business_unit) params.append('business_unit', filterObj.business_unit);
    if (filterObj.addressability) params.append('addressability', filterObj.addressability);
    if (filterObj.fiscal_year) params.append('fiscal_year', filterObj.fiscal_year);
    if (filterObj.startDate) params.append('start_date', filterObj.startDate.split('T')[0]);
    if (filterObj.endDate) params.append('end_date', filterObj.endDate.split('T')[0]);
    return params.toString();
  }, []);

  const fetchDashboardData = useCallback(async (filterObj = filters) => {
    try {
      const queryString = buildQueryString(filterObj);
      const [statsRes, trendsRes] = await Promise.all([
        api.get(`/dashboard/stats${queryString ? `?${queryString}` : ''}`),
        api.get(`/analytics/trends${queryString ? `?${queryString}` : ''}`),
      ]);
      setStats(statsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQueryString, filters]);

  const fetchPaymentTerms = async (filterObj = filters) => {
    try {
      const qs = buildQueryString(filterObj);
      const response = await api.get(`/analytics/payment-terms${qs ? `?${qs}` : ''}`);
      setPaymentTerms(response.data);
    } catch (error) {
      console.error('Failed to load payment terms');
    }
  };

  const fetchSavingsPotential = async (force = false) => {
    try {
      // Cold cache paths run a synchronous LLM call (~30-60s), so this specific
      // endpoint needs a generous timeout — otherwise browsers fall through the
      // default XHR timeout and the user sees a misleading "unavailable" error
      // even though the backend actually finishes and caches the result.
      const response = await api.get(
        `/ai/savings-summary${force ? '?force=true' : ''}`,
        { timeout: 90000 },
      );
      setSavingsPotential(response.data);
    } catch (err) {
      if (err.response?.status === 422) {
        const detail = err.response.data?.detail;
        setSavingsPotential({
          has_insights: false,
          pds_blocked: true,
          pds_score: detail?.pds_score || 0,
          message: detail?.message || 'Data quality too low for AI analysis',
        });
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setSavingsPotential({
          has_insights: false,
          pds_blocked: false,
          message: 'AI analysis is generating — refresh in a minute',
        });
      } else {
        setSavingsPotential({ has_insights: false, pds_blocked: false, message: 'AI analysis unavailable' });
      }
    }
  };

  const fetchP2pSummary = async (filterObj = filters) => {
    try {
      const qs = buildQueryString(filterObj);
      const response = await api.get(`/analytics/p2p-summary${qs ? `?${qs}` : ''}`);
      setP2pSummary(response.data);
    } catch (error) {
      console.error('Failed to load P2P summary');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchPaymentTerms();
    fetchSavingsPotential();
    fetchP2pSummary();
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setRefreshing(true);
    fetchDashboardData(newFilters);
    fetchPaymentTerms(newFilters);
    fetchSavingsPotential();
    fetchP2pSummary(newFilters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData(filters);
    fetchPaymentTerms(filters);
    fetchSavingsPotential(true);
    fetchP2pSummary(filters);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const categoryData = stats?.category_spend
    ? Object.entries(stats.category_spend).map(([name, value]) => ({ name, value }))
    : [];

  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== '' && v !== 'all').length;

  const fmtMoney = (v) => `${cs}${Number(v || 0).toLocaleString()}`;
  const savingsRows = () => savingsPotential?.has_insights ? [
    ['Savings Potential (mid)', fmtMoney(savingsPotential.total_savings_mid)],
    ['Savings Range', `${fmtMoney(savingsPotential.total_savings_low)} - ${fmtMoney(savingsPotential.total_savings_high)}`],
  ] : [];
  const p2pRows = () => (hasP2P && p2pSummary?.has_data) ? [
    ['Purchase Orders', String(p2pSummary.unique_pos ?? 0)],
    ['PO Coverage (% of transactions)', `${p2pSummary.po_coverage_pct ?? 0}%`],
    ['2-Way Match (% of transactions)', `${p2pSummary.two_way_match_rate ?? 0}%`],
    ['3-Way Match (% of transactions)', `${p2pSummary.three_way_match_rate ?? 0}%`],
    ['Contracted (% of spend)', `${p2pSummary.contracted_pct ?? 0}%`],
    ['Non-PO Spend', fmtMoney(p2pSummary.non_po_spend)],
    ['Non-PO (% of spend)', `${Number(p2pSummary.non_po_spend_pct ?? 0).toFixed(1)}%`],
  ] : [];
  const maverickRows = () => [
    ['Maverick composite (% of spend)', `${stats?.maverick_percentage?.toFixed(1) ?? 0}%`],
    ['  Maverick composite (% of transactions)', `${stats?.maverick_breakdown?.composite_pct?.toFixed(1) ?? 0}%`],
    ['  Off-contract (% of transactions)', `${stats?.maverick_breakdown?.off_contract_pct?.toFixed(1) ?? 0}%`],
    ['  Non-PO (% of transactions)', `${stats?.maverick_breakdown?.non_po_pct?.toFixed(1) ?? 0}%`],
  ];

  const exportCSV = () => {
    const rows = [
      ['--- KPI Summary ---'],
      ['Metric', 'Value'],
      ['Total Spend', `${cs}${stats?.total_spend?.toLocaleString() || 0}`],
      ['Active Suppliers', stats?.supplier_count || 0],
      ...maverickRows(),
      ['Transactions', stats?.transaction_count || 0],
      ...savingsRows(),
      ...(p2pRows().length ? [[], ['--- P2P Overview ---'], ['Metric', 'Value'], ...p2pRows()] : []),
      [],
      ['--- Spend Trends ---'],
      ['Month', 'Spend'],
      ...trends.map(t => [t.month, t.spend]),
      [],
      ['--- Category Distribution ---'],
      ['Category', 'Spend'],
      ...categoryData.map(c => [c.name, c.value]),
      [],
      ['--- Top 5 Suppliers ---'],
      ['Supplier', 'Category', 'Total Spend'],
      ...(stats?.top_suppliers || []).map(s => [s.name, s.category || 'N/A', s.total_spend]),
    ];
    if (paymentTerms?.distribution) {
      rows.push([], ['--- Payment Terms ---'], ['Term', 'Spend', '% of Total', 'Transactions']);
      paymentTerms.distribution.forEach(p => rows.push([p.name, p.value, `${p.spend_percentage}%`, p.transaction_count]));
    }
    downloadCSV(rows, 'dashboard_overview');
  };

  const exportPDF = () => {
    const sections = [
      { title: 'KPI Summary', headers: ['Metric', 'Value'], rows: [['Total Spend', `${cs}${stats?.total_spend?.toLocaleString() || 0}`], ['Active Suppliers', String(stats?.supplier_count || 0)], ...maverickRows(), ['Transactions', String(stats?.transaction_count || 0)], ...savingsRows()] },
      ...(p2pRows().length ? [{ title: 'P2P Overview', headers: ['Metric', 'Value'], rows: p2pRows() }] : []),
      { title: 'Spend Trends', headers: ['Month', 'Spend'], rows: trends.map(t => [t.month, `${cs}${t.spend?.toLocaleString()}`]) },
      { title: 'Category Distribution', headers: ['Category', 'Spend'], rows: categoryData.map(c => [c.name, `${cs}${c.value?.toLocaleString()}`]) },
      { title: 'Top 5 Suppliers', headers: ['Supplier', 'Category', 'Total Spend'], rows: (stats?.top_suppliers || []).map(s => [s.name, s.category || 'N/A', `${cs}${s.total_spend?.toLocaleString()}`]) },
    ];
    if (paymentTerms?.distribution) {
      sections.push({ title: 'Payment Terms', headers: ['Term', 'Spend', '% of Total', 'Transactions'], rows: paymentTerms.distribution.map(p => [p.name, `${cs}${p.value?.toLocaleString()}`, `${p.spend_percentage}%`, String(p.transaction_count)]) });
    }
    buildPDF(sections, 'Dashboard_Overview');
  };

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Spend Overview</h1>
          <p className="text-muted-foreground mt-1">
            Monitor your spend and identify opportunities
            {activeFiltersCount > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="dashboard" />
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh} 
            disabled={refreshing}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Dashboard sub-page tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="overview" data-testid="dashboard-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="signals" data-testid="dashboard-tab-signals">Signal Intelligence</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'signals' && <SignalsPage embedded />}

      <div className={activeTab === 'overview' ? 'space-y-4' : 'hidden'}>
      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageName="dashboard"
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'department', 'cost_center', 'business_unit']}
        showDateRange={true}
        compact={true}
      />

      {/* Signal Intelligence — investigation priorities */}
      <SignalsWidget />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-l-4 border-l-blue-600 hover:shadow-lg transition-shadow duration-200" data-testid="total-spend-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                TOTAL SPEND
                <HelpBubble text="Sum of all transaction amounts, normalised to your base currency using the tenant-fixed FX rates. Includes every row in the currently filtered view." />
              </CardTitle>
              <DollarSign className="w-5 h-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold font-mono tracking-tight">
                {cs}{stats?.total_spend?.toLocaleString() || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{Object.keys(stats?.category_spend || {}).length} categories</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}>
          <Card className="border-l-4 border-l-violet-500 hover:shadow-lg transition-shadow duration-200" data-testid="total-transactions-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                TOTAL TRANSACTIONS
                <HelpBubble text="Number of transaction rows in the current view. Sub-text shows the mean value per transaction — a low value can hint at fragmented buying." />
              </CardTitle>
              <CreditCard className="w-5 h-5 text-violet-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold font-mono tracking-tight">{stats?.transaction_count?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">Avg {cs}{stats?.transaction_count ? Math.round(stats.total_spend / stats.transaction_count).toLocaleString() : 0}/txn</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="border-l-4 border-l-cyan-500 hover:shadow-lg transition-shadow duration-200" data-testid="suppliers-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                ACTIVE SUPPLIERS
                <HelpBubble text="Distinct suppliers that appear at least once in the filtered transactions. Country count under this reflects supplier country, not spend location." />
              </CardTitle>
              <Users className="w-5 h-5 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold font-mono tracking-tight">{stats?.supplier_count?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">Across {stats?.spend_by_country?.length || 0} countries</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow duration-200 cursor-help" data-testid="maverick-spend-card">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    MAVERICK SPEND
                    <Info className="w-3 h-3 text-muted-foreground/60" />
                  </CardTitle>
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold font-mono tracking-tight">{stats?.maverick_percentage?.toFixed(1) || 0}%</div>
                  <p className="text-xs text-muted-foreground mt-2">% of spend · composite leakage (hover for breakdown)</p>
                </CardContent>
              </Card>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 bg-primary text-primary-foreground border-primary" side="bottom" align="end" data-testid="maverick-tooltip">
              <div className="space-y-2 text-xs">
                <div className="font-semibold text-sm flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-300" />
                  Maverick Spend (Composite)
                </div>
                <p className="text-[hsl(var(--primary-foreground)/0.85)] leading-relaxed">
                  Share of activity that is <strong>NOT</strong> both contracted <strong>AND</strong> backed by a PO — the industry-standard headline for procurement leakage.
                </p>
                <div className="border-t border-[hsl(var(--primary-foreground)/0.25)] pt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-[hsl(var(--primary-foreground)/0.85)]">Composite · % of spend:</span>
                    <span className="font-semibold font-mono" data-testid="maverick-composite-spend-pct">{stats?.maverick_percentage?.toFixed(1) ?? 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[hsl(var(--primary-foreground)/0.85)]">Composite · % of transactions:</span>
                    <span className="font-mono" data-testid="maverick-composite-count-pct">{stats?.maverick_breakdown?.composite_pct?.toFixed(1) ?? 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[hsl(var(--primary-foreground)/0.85)]">• Off-contract · % of transactions:</span>
                    <span className="font-mono">{stats?.maverick_breakdown?.off_contract_pct?.toFixed(1) ?? 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[hsl(var(--primary-foreground)/0.85)]">• Non-PO · % of transactions:</span>
                    <span className="font-mono">{stats?.maverick_breakdown?.non_po_pct?.toFixed(1) ?? 0}%</span>
                  </div>
                </div>
                <p className="text-[10px] text-[hsl(var(--primary-foreground)/0.7)] italic border-t border-[hsl(var(--primary-foreground)/0.25)] pt-2">
                  Off-contract = commercial leakage (no framework agreement). Non-PO = process leakage (skipped PR-to-PO).
                </p>
              </div>
            </HoverCardContent>
          </HoverCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow duration-200" data-testid="savings-potential-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">SAVINGS POTENTIAL</CardTitle>
              <div className="flex items-center gap-1">
                <HelpBubble text="AI estimate of achievable savings from the current view: consolidation opportunities, tail-spend rationalisation, and payment-term optimisation. Sized on addressable spend only (Non-Addressable spend such as salaries is excluded). Mid-point of a low/high range — see the Reports > Savings Potential Report for the detailed breakdown." />
                <TrendingDown className="w-5 h-5 text-emerald-500" />
              </div>
            </CardHeader>
            <CardContent>
              {savingsPotential?.has_insights ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <div className="text-xl font-bold font-mono tracking-tight text-emerald-600">
                      {cs}{savingsPotential.total_savings_mid >= 1e9
                        ? `${(savingsPotential.total_savings_mid / 1e9).toFixed(1)}B`
                        : savingsPotential.total_savings_mid >= 1e6
                          ? `${(savingsPotential.total_savings_mid / 1e6).toFixed(1)}M`
                          : `${(savingsPotential.total_savings_mid / 1e3).toFixed(0)}K`}
                    </div>
                    {savingsPotential.stale && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground px-1.5 py-0.5 rounded-full bg-muted"
                        title="This figure is >24h old — a fresh analysis is regenerating in the background. Refresh in a minute to see the update."
                        data-testid="savings-refreshing-chip"
                      >
                        <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                        refreshing…
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{savingsPotential.summary}</p>
                  <p className="text-xs text-emerald-600 mt-1">
                    Range: {cs}{savingsPotential.total_savings_low >= 1e9
                      ? `${(savingsPotential.total_savings_low / 1e9).toFixed(1)}B`
                      : `${(savingsPotential.total_savings_low / 1e6).toFixed(1)}M`}
                    {' - '}{cs}{savingsPotential.total_savings_high >= 1e9
                      ? `${(savingsPotential.total_savings_high / 1e9).toFixed(1)}B`
                      : `${(savingsPotential.total_savings_high / 1e6).toFixed(1)}M`}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1" data-testid="savings-basis-note" title={savingsBasisNote(savingsPotential, cs)}>
                    {savingsBasisNote(savingsPotential, cs)}
                  </p>
                  {savingsPotential.pds_warning && (
                    <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Results may be less accurate (PDS: {savingsPotential.pds_score}%)
                    </p>
                  )}
                </>
              ) : savingsPotential ? (
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    {savingsPotential.pds_blocked
                      ? `PDS Score: ${savingsPotential.pds_score}%`
                      : 'Unavailable'}
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    {savingsPotential.pds_blocked
                      ? 'Improve data quality to 40%+ to unlock AI insights'
                      : savingsPotential.message}
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-xl font-bold font-mono tracking-tight text-muted-foreground">
                    <div className="h-5 w-16 bg-muted animate-pulse rounded" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Analyzing spend data...</p>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* P2P Summary Strip */}
      {hasP2P && p2pSummary?.has_data && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
          <Card data-testid="p2p-summary-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ArrowRightLeft className="w-5 h-5 text-primary" />
                P2P Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> Purchase Orders</p>
                  <p className="text-xl font-bold font-mono text-blue-700 dark:text-blue-400" data-testid="p2p-po-count">{p2pSummary.unique_pos?.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">{p2pSummary.po_coverage_pct}% of transactions</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3" /> 2-Way Match</p>
                  <p className="text-xl font-bold font-mono text-emerald-700 dark:text-emerald-400" data-testid="p2p-match-rate">{p2pSummary.two_way_match_rate}%</p>
                  <p className="text-[10px] text-muted-foreground">PO-Invoice · % of transactions</p>
                </div>
                <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3" /> 3-Way Match</p>
                  <p className="text-xl font-bold font-mono text-violet-700 dark:text-violet-400" data-testid="p2p-3way-rate">{p2pSummary.three_way_match_rate}%</p>
                  <p className="text-[10px] text-muted-foreground">PO+GRN+Invoice · % of transactions</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-muted-foreground">Contracted Spend</p>
                  <p className="text-xl font-bold font-mono text-amber-700 dark:text-amber-400" data-testid="p2p-contracted">{p2pSummary.contracted_pct}%</p>
                  <p className="text-[10px] text-muted-foreground">on-contract · % of spend</p>
                </div>
                <div className={`p-3 rounded-lg ${p2pSummary.maverick_pct > 15 ? 'bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800' : 'bg-muted/50 border'}`}>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Non-PO Spend</p>
                  <p className={`text-xl font-bold font-mono ${p2pSummary.maverick_pct > 15 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`} data-testid="p2p-non-po">
                    {cs}{(p2pSummary.non_po_spend / 1e6).toFixed(1)}m
                  </p>
                  <p className="text-[10px] text-muted-foreground">{Number(p2pSummary.non_po_spend_pct ?? 0).toFixed(1)}% of total spend</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Spend Trend */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card data-testid="spend-trend-chart">
            <CardHeader>
              <CardTitle>Spend Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${cs}${(v/1000000).toFixed(1)}M`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`${cs}${value.toLocaleString()}`, 'Spend']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="spend" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} name="Total Spend" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Distribution (extracted memoized sub-component) */}
        <CategoryPieChart data={categoryData} colors={COLORS} cs={cs} delay={0.6} />
      </div>

      {/* Spend by Country + Payment Terms — side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Spend by Country — Horizontal Bar */}
        {stats?.spend_by_country?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.62 }}>
            <Card data-testid="spend-by-country-chart" className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-emerald-500" />
                  Spend by Country
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.spend_by_country} layout="vertical" margin={{ left: 5, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11}
                      tickFormatter={v => v >= 1e6 ? `${cs}${(v / 1e6).toFixed(0)}M` : `${cs}${(v / 1e3).toFixed(0)}K`} />
                    <YAxis dataKey="name" type="category" width={80} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      formatter={(value, name, props) => [`${cs}${value?.toLocaleString()} (${props.payload.pct}%)`, 'Spend']}
                    />
                    <Bar dataKey="spend" radius={[0, 6, 6, 0]}>
                      {stats.spend_by_country.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Payment Terms — Donut */}
        {paymentTerms && paymentTerms.distribution && paymentTerms.distribution.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
            <Card data-testid="payment-terms-chart" className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Terms
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {paymentTerms.terms_count} terms across {paymentTerms.total_transactions} transactions
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={paymentTerms.distribution.slice(0, 8)} cx="50%" cy="50%"
                          innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                          {paymentTerms.distribution.slice(0, 8).map((_, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
                                <p className="font-semibold">{data.name}</p>
                                <p className="text-sm">{cs}{data.value?.toLocaleString()}</p>
                                <p className="text-sm text-muted-foreground">{data.spend_percentage}%</p>
                              </div>
                            );
                          }
                          return null;
                        }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-1.5 min-w-[140px] pt-2">
                    {paymentTerms.distribution.slice(0, 6).map((item, index) => (
                      <div key={item.name} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span className="text-muted-foreground truncate">{item.name}</span>
                        <span className="font-mono ml-auto">{item.spend_percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Top Suppliers */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
        <Card data-testid="top-suppliers-section">
          <CardHeader>
            <CardTitle>Top 5 Suppliers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.top_suppliers?.map((supplier, index) => (
                <div key={supplier.name || supplier.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors duration-200">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold">{supplier.name}</p>
                      <p className="text-sm text-muted-foreground">{supplier.category || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold">{cs}{supplier.total_spend?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total spend</p>
                  </div>
                </div>
              ))}
              {(!stats?.top_suppliers || stats.top_suppliers.length === 0) && (
                <p className="text-center text-muted-foreground py-8">No supplier data available. Upload data to see insights.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* AI Recommendations */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <AIPanel page="dashboard" filters={filters} insightsMode="digest" />
      </motion.div>

      {/* ML Insights */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <MLInsightsPanel />
      </motion.div>
      </div>
    </div>
  );
}
